import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { reportsApi as R, fmt } from "@/lib/reports";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Download, Printer, BarChart3 } from "lucide-react";

export default function Reports() {
  const { can } = useAuth();
  const canExport = can("reports", "export");
  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState(null);
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    R.catalog().then((c) => {
      setCatalog(c);
      const first = c[0]?.reports[0];
      if (first) setActive(first);
    }).catch(() => toast.error("Failed to load report catalog"));
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  const runReport = async (rep, f) => {
    if (!rep) return;
    setLoading(true);
    try {
      const params = {};
      if (f.from) params.from_date = f.from;
      if (f.to) params.to_date = f.to;
      if (f.customer_id) params.customer_id = f.customer_id;
      if (f.supplier_id) params.supplier_id = f.supplier_id;
      if (f.status) params.status = f.status;
      setData(await R.run(rep.key, params));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to run report");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (active) { setFilters({}); runReport(active, {}); } /* eslint-disable-next-line */ }, [active]);

  const params = useMemo(() => {
    const p = {};
    if (filters.from) p.from_date = filters.from;
    if (filters.to) p.to_date = filters.to;
    if (filters.customer_id) p.customer_id = filters.customer_id;
    if (filters.supplier_id) p.supplier_id = filters.supplier_id;
    if (filters.status) p.status = filters.status;
    return p;
  }, [filters]);

  const doExport = async () => {
    try { await R.exportCsv(active.key, params); toast.success("Exported CSV"); }
    catch (e) { toast.error("Export failed"); }
  };

  const has = (t) => active?.filters?.includes(t);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Reports &amp; Analytics</h2>
        <p className="mt-1 text-sm text-slate-500">Cross-module reporting with date, customer &amp; supplier filters. Export to Excel (CSV) or print.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        <Card className="p-3 self-start print:hidden" data-testid="report-catalog">
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {catalog.map((c) => (
              <div key={c.category}>
                <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{c.category}</p>
                <div className="space-y-0.5">
                  {c.reports.map((rep) => (
                    <button key={rep.key} onClick={() => setActive(rep)} data-testid={`report-${rep.key}`}
                      className={`w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors ${active?.key === rep.key ? "bg-indigo-50 font-medium text-indigo-700" : "text-slate-600 hover:bg-slate-50"}`}>
                      {rep.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-4 print:hidden">
            <div className="flex flex-wrap items-end gap-3">
              {has("date") && <>
                <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={filters.from || ""} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="w-40" data-testid="report-from" /></div>
                <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={filters.to || ""} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="w-40" data-testid="report-to" /></div>
              </>}
              {has("customer") && (
                <div className="space-y-1"><Label className="text-xs">Customer</Label>
                  <Select value={filters.customer_id || "all"} onValueChange={(v) => setFilters({ ...filters, customer_id: v === "all" ? "" : v })}>
                    <SelectTrigger className="w-52" data-testid="report-customer"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All customers</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
              {has("supplier") && (
                <div className="space-y-1"><Label className="text-xs">Supplier</Label>
                  <Select value={filters.supplier_id || "all"} onValueChange={(v) => setFilters({ ...filters, supplier_id: v === "all" ? "" : v })}>
                    <SelectTrigger className="w-52" data-testid="report-supplier"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All suppliers</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
              {has("status") && (
                <div className="space-y-1"><Label className="text-xs">Status</Label><Input value={filters.status || ""} onChange={(e) => setFilters({ ...filters, status: e.target.value })} placeholder="e.g. Confirmed" className="w-40" data-testid="report-status" /></div>
              )}
              <Button onClick={() => runReport(active, filters)} disabled={loading} data-testid="run-report-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
              <div className="flex-1" />
              {canExport && <Button variant="outline" onClick={doExport} data-testid="export-report-btn"><Download className="mr-2 h-4 w-4" />Export</Button>}
              <Button variant="outline" onClick={() => window.print()} data-testid="print-report-btn"><Printer className="mr-2 h-4 w-4" />Print</Button>
            </div>
          </Card>

          <Card className="p-4" data-testid="report-result">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><BarChart3 className="h-5 w-5 text-indigo-600" />{data?.label || active?.label}</h3>
              {data?.summary && (
                <div className="text-right text-sm">
                  <span className="text-slate-500">Records: <span className="font-semibold text-slate-800" data-testid="report-count">{data.summary.count}</span></span>
                  {data.summary.total !== undefined && <span className="ml-4 text-slate-500">{data.summary.total_label || "Total"}: <span className="font-semibold text-slate-900" data-testid="report-total">{fmt(data.summary.total)}</span></span>}
                </div>
              )}
            </div>
            {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : !data ? null : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">{data.columns.map((c) => <TableHead key={c} className="text-xs uppercase text-slate-500">{c}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {data.rows.map((row, i) => (
                      <TableRow key={i} data-testid={`report-row-${i}`}>{data.columns.map((c) => <TableCell key={c} className="text-slate-700">{fmt(row[c])}</TableCell>)}</TableRow>
                    ))}
                    {data.rows.length === 0 && <TableRow><TableCell colSpan={data.columns.length} className="py-12 text-center text-slate-400" data-testid="report-empty">No data for the selected filters.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
